import React from 'react';
import { motion } from 'framer-motion';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import {
  Calendar, Video, CreditCard, Users, FileText,
  BarChart3, Shield, Smartphone, Settings, Clock
} from 'lucide-react';

const platformFeatures = [
  {
    icon: Calendar,
    title: 'Zero Click Scheduling',
    description: 'AI matches patients with counselors by availability and specialization. Automated reminders reduce no-shows.',
  },
  {
    icon: Video,
    title: 'Native Telehealth',
    description: 'HIPAA-compliant video sessions built into the platform. Launch sessions from the dashboard with concurrent AI documentation.',
  },
  {
    icon: CreditCard,
    title: 'Claims & Billing',
    description: 'CMS-1500 generation, ICD code extraction, ERA processing, and denial risk flagging. All automated.',
  },
  {
    icon: Users,
    title: 'Client Portal',
    description: 'Secure patient interface for session viewing, progress tracking, care plan access, and assessment completion.',
  },
  {
    icon: FileText,
    title: 'Clinical Assessments',
    description: 'Built-in PHQ-9, GAD-7, and PTSD screeners with automatic AI scoring, analysis, and risk flagging.',
  },
  {
    icon: BarChart3,
    title: 'Real-Time Analytics',
    description: 'Metrics for reporting, patient progress, outcomes tracking, and cost management across your entire practice.',
  },
  {
    icon: Shield,
    title: 'Compliance Engine',
    description: 'HIPAA-compliant from day one. End-to-end encryption, CMS BHI standards, and audit-ready documentation.',
  },
  {
    icon: Smartphone,
    title: 'Mobile Apps',
    description: 'Integrated patient and clinician mobile apps for care management on the go.',
  },
  {
    icon: Settings,
    title: 'EHR Integration',
    description: 'Seamless integration with existing EHR systems. Customizable clinic branding and workflows.',
  },
  {
    icon: Clock,
    title: 'Care Navigation',
    description: 'Track patient engagement, care plan adherence, and coordinate multi-provider care teams efficiently.',
  },
];

export default function PlatformFeatures() {
  const { ref, inView } = useScrollAnimation();

  return (
    <section className="section-padding bg-white" ref={ref}>
      <div className="container-max">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-gray-900 mb-4 tracking-tight">
            Everything You Need.{' '}
            <span className="gradient-text">Nothing You Don't.</span>
          </h2>
          <p className="text-lg text-gray-600">
            Beyond AI innovation, Doctorite delivers a complete practice management platform
            with all the tools behavioral health providers expect and more.
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          {platformFeatures.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.4, delay: index * 0.05 }}
              className="group p-5 rounded-2xl border border-gray-100 hover:border-brand-200 hover:shadow-lg hover:shadow-brand-900/5 transition-all duration-300 bg-white"
            >
              <div className="w-10 h-10 bg-brand-50 rounded-xl flex items-center justify-center mb-3 group-hover:bg-brand-100 transition-colors">
                <feature.icon className="w-5 h-5 text-brand-600" />
              </div>
              <h3 className="text-sm font-bold text-gray-900 mb-1.5">{feature.title}</h3>
              <p className="text-xs text-gray-500 leading-relaxed">{feature.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
